import type { AiCost } from '../ai/ui/CostTag';
import { apiRequest } from '@/lib/api-client';
import { env } from '@/config/env';

export type MovieReviewStatus =
  | 'scripting'
  | 'awaiting_footage'
  | 'queued'
  | 'assembling'
  | 'ready'
  | 'failed';

export interface ReviewScore {
  label: string;
  value: number;
}

export interface ReviewPublishRef {
  platform: 'meta';
  ref: string;
  url: string | null;
  publishedAt: string;
}

export interface MovieReviewSource {
  url: string;
  label: string;
  /** The link it was fetched from; '' for a direct upload. */
  origin: string;
  durationSec: number;
}

export interface MovieReview {
  id: string;
  title: string;
  year: string;
  director: string;
  wikidataId: string;
  /** What the film is — leads the published caption. */
  description: string;
  /** ISO "YYYY-MM-DD"; '' when unknown. */
  releaseDate: string;
  scores: ReviewScore[];
  overall: string;
  verdict: string;
  caption: string;
  hashtags: string[];
  /** Every clip the montage is cut from. */
  sources: MovieReviewSource[];
  footageUrl: string | null;
  /** A track for this review alone; null means the channel's choice applies. */
  musicUrl: string | null;
  videoUrl: string | null;
  status: MovieReviewStatus;
  published: ReviewPublishRef[];
  /** ISO time a scheduled publish is due; null when nothing is queued. */
  publishAt: string | null;
  error: string | null;
  cost: AiCost | null;
  createdAt: string;
}

const base = (orgId: string, projectId: string): string =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/movie-reviews`;

/** CSRF token from the cookie the API sets — the upload bypasses apiRequest. */
const csrfToken = (): string =>
  document.cookie
    .split('; ')
    .find((c) => c.startsWith('csrf_token='))
    ?.slice('csrf_token='.length) ?? '';

/**
 * Uploads a file as a RAW body rather than multipart or JSON. The API pipes the
 * request straight into storage, so the file never has to be held in memory on
 * either side — which base64-in-JSON would force, at +33% size.
 *
 * Uses XMLHttpRequest rather than fetch purely for `upload.onprogress`: fetch
 * still cannot report upload progress, and a video large enough to take a while
 * is exactly the case where a bare spinner reads as a hang. The render itself is
 * a background agent — this request only covers the byte transfer, which nothing
 * can move off the critical path short of uploading direct to object storage.
 */
const rawUpload = <T>(
  path: string,
  file: File,
  fallbackType: string,
  headers: Record<string, string>,
  onProgress?: (percent: number) => void,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // Respect VITE_API_URL like every other call. Left relative, an upload
    // always goes same-origin — through the frontend's nginx and its 1MB
    // default body limit — even when the app is pointed at the API directly.
    xhr.open('POST', `${env.apiUrl}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', file.type || fallbackType);
    xhr.setRequestHeader('x-csrf-token', csrfToken());
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);

    // A proxy that refuses the body past its size limit simply stops reading:
    // the browser's send buffer fills, onprogress freezes, and the request
    // hangs forever with no error at all. Surface that rather than spinning —
    // the usual cause is a proxy request-size limit (client_max_body_size).
    let lastLoaded = 0;
    let lastMoved = Date.now();
    let stalled = false;
    const stallCheck = setInterval(() => {
      if (Date.now() - lastMoved > 30_000) {
        clearInterval(stallCheck);
        stalled = true;
        xhr.abort();
      }
    }, 5000);
    const done = (): void => clearInterval(stallCheck);
    xhr.upload.onprogress = (e) => {
      if (e.loaded !== lastLoaded) {
        lastLoaded = e.loaded;
        lastMoved = Date.now();
      }
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      done();
      let payload: { data?: T; error?: { message?: string } } = {};
      try {
        payload = JSON.parse(xhr.responseText) as typeof payload;
      } catch {
        // Non-JSON body (a proxy error page, say) — fall through to the status.
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload.data !== undefined)
        resolve(payload.data);
      else reject(new Error(payload.error?.message ?? `Upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => {
      done();
      reject(new Error('Upload failed — check your connection'));
    };
    xhr.onabort = () => {
      done();
      reject(
        new Error(
          stalled
            ? 'Upload stalled — the server stopped accepting data, usually a proxy request-size limit.'
            : 'Upload cancelled',
        ),
      );
    };
    xhr.send(file);
  });

/** Clips that could not be fetched, reported alongside the ones that could. */
export interface LinkFailure {
  link: string;
  reason: string;
}

export const movieReviewApi = {
  list: (orgId: string, projectId: string) => apiRequest<MovieReview[]>(base(orgId, projectId)),
  create: (orgId: string, projectId: string) =>
    apiRequest<MovieReview>(base(orgId, projectId), { method: 'POST' }),
  remove: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/${id}`, { method: 'DELETE' }),
  retry: (orgId: string, projectId: string, id: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/retry`, { method: 'POST' }),
  publish: (orgId: string, projectId: string, id: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/publish`, { method: 'POST' }),

  /** Queues the Reel to go out at `publishAt` (an absolute ISO instant). */
  schedule: (orgId: string, projectId: string, id: string, publishAt: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/schedule`, {
      method: 'POST',
      body: { publishAt },
    }),

  /** Replaces the synopsis that leads the published caption. */
  updateDescription: (orgId: string, projectId: string, id: string, description: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/description`, {
      method: 'PATCH',
      body: { description },
    }),

  cancelSchedule: (orgId: string, projectId: string, id: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/schedule`, { method: 'DELETE' }),

  /** Appends one clip to the review. Several calls build the set. */
  uploadFootage: (
    orgId: string,
    projectId: string,
    id: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<MovieReview> =>
    rawUpload<MovieReview>(
      `${base(orgId, projectId)}/${id}/footage`,
      file,
      'video/mp4',
      // The clip's name can't ride in the body — the body is the video.
      { 'x-clip-name': encodeURIComponent(file.name).replace(/%[0-9A-F]{2}/g, '') },
      onProgress,
    ),

  /**
   * Attaches clips from links, for footage that lives on a remote server. The
   * server pulls each one down; the response says which ones it could not.
   */
  addLinks: (orgId: string, projectId: string, id: string, links: string) =>
    apiRequest<{ review: MovieReview; failures: LinkFailure[] }>(
      `${base(orgId, projectId)}/${id}/sources/links`,
      { method: 'POST', body: { links } },
    ),

  removeSource: (orgId: string, projectId: string, id: string, index: number) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/sources/${String(index)}`, {
      method: 'DELETE',
    }),

  /** Starts the build: analyse the clips, cut the montage, composite the Reel. */
  render: (orgId: string, projectId: string, id: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/render`, { method: 'POST' }),

  /** A track for this review alone, overriding the channel's music. */
  uploadMusic: (
    orgId: string,
    projectId: string,
    id: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<MovieReview> =>
    rawUpload<MovieReview>(
      `${base(orgId, projectId)}/${id}/music`,
      file,
      'audio/mpeg',
      {},
      onProgress,
    ),

  clearMusic: (orgId: string, projectId: string, id: string) =>
    apiRequest<MovieReview>(`${base(orgId, projectId)}/${id}/music`, { method: 'DELETE' }),
};

export interface MovieReviewChannel {
  autoPublish: boolean;
  /** "HH:mm" wall-clock in publishTimezone. */
  publishTime: string;
  publishTimezone: string;
  /** The channel's own track; null means the generated theme is used. */
  musicUrl: string | null;
  musicName: string;
}

export const movieReviewChannelApi = {
  get: (orgId: string, projectId: string) =>
    apiRequest<MovieReviewChannel>(`${base(orgId, projectId)}/channel`),

  update: (orgId: string, projectId: string, input: Partial<MovieReviewChannel>) =>
    apiRequest<MovieReviewChannel>(`${base(orgId, projectId)}/channel`, {
      method: 'PUT',
      body: input,
    }),

  /** Uploads the track every review on this channel uses. */
  uploadMusic: (
    orgId: string,
    projectId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<MovieReviewChannel> =>
    rawUpload<MovieReviewChannel>(
      `${base(orgId, projectId)}/channel/music`,
      file,
      'audio/mpeg',
      { 'x-track-name': encodeURIComponent(file.name).replace(/%[0-9A-F]{2}/g, '') },
      onProgress,
    ),

  /** Drops the custom track, falling back to the generated theme. */
  clearMusic: (orgId: string, projectId: string) =>
    apiRequest<MovieReviewChannel>(`${base(orgId, projectId)}/channel/music`, { method: 'DELETE' }),
};
